package com.tregz.robbins

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.*
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntSize
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlin.math.cos
import kotlin.math.sin
import kotlin.random.Random

private data class Node(
    val x: Float,
    val y: Float,
    val angle: Float,
    val radius: Float,
    val curve: Float? = null
)

@Composable
fun Forest(modifier: Modifier = Modifier) {
    val density = LocalDensity.current
    
    BoxWithConstraints(modifier = modifier.fillMaxSize()) {
        val width = maxWidth.value
        val height = maxHeight.value

        val bitmap = remember(width, height) {
            if (width > 0 && height > 0) {
                ImageBitmap(
                    (width * density.density).toInt(),
                    (height * density.density).toInt()
                )
            } else null
        }

        // Use a heartbeat to refresh the UI at a steady 60fps instead of every single draw call
        val frameTrigger = remember { mutableStateOf(0L) }

        if (bitmap != null) {
            Canvas(modifier = Modifier.fillMaxSize()) {
                frameTrigger.value // Observe heartbeat
                drawImage(bitmap, dstSize = IntSize(size.width.toInt(), size.height.toInt()))
            }

            LaunchedEffect(bitmap) {
                // Heartbeat loop: update UI 60 times per second
                launch {
                    while (isActive) {
                        withFrameMillis { frameTrigger.value = it }
                    }
                }

                val canvas = Canvas(bitmap)
                val paint = Paint().apply {
                    isAntiAlias = true
                    style = PaintingStyle.Stroke
                }
                
                canvas.save()
                canvas.scale(density.density, density.density)

                fun drawPersistent(color: Color, width: Float, block: Path.() -> Unit) {
                    paint.color = color
                    paint.strokeWidth = width
                    val path = Path().apply(block)
                    canvas.drawPath(path, paint)
                }

                val foliageNodes = mutableListOf<List<Node>>()
                val count = (width / 20).toInt()
                
                for (i in 0 until count) {
                    launch {
                        delay(50L * i)
                        tree(width, height, foliageNodes) { c, w, b -> drawPersistent(c, w, b) }
                    }
                }
                
                delay(count * 50L + 2000L)
                foliage(foliageNodes) { c, w, b -> drawPersistent(c, w, b) }
            }
        }
    }
}

private fun angle(previous: Float, height: Float): Float {
    return previous + real(height / 100f + next(50))
}

private fun branch(
    node: Node,
    curve: Float,
    sizing: Float,
    color: Color,
    height: Float,
    draw: (Color, Float, Path.() -> Unit) -> Unit
): Node {
    var reduction = Random.nextFloat() * 2f
    if (reduction > 1f) reduction = 1f
    val radius = node.radius * reduction
    val a = angle(node.angle, height)
    val multiplier = (a * kotlin.math.PI / 180f).toFloat() + real(Random.nextFloat() / 2f)
    val x = node.x + sizing * cos(multiplier)
    val y = node.y - sizing * sin(multiplier)
    val end = Node(x, y, a, radius, curve)
    
    draw(color, radius) {
        moveTo(node.x, node.y)
        cubicTo(node.x, node.y, node.x + curve, node.y + curve, x, y)
    }
    return end
}

private fun branches(
    nodes: List<Node>,
    color: Color,
    sizing: Float,
    foliage: MutableList<List<Node>>,
    height: Float,
    draw: (Color, Float, Path.() -> Unit) -> Unit
) {
    val reduction = (next(20) + 50) / 100f
    val smaller = sizing * reduction
    val list = mutableListOf<Node>()
    
    nodes.forEach { node ->
        val base = node.copy(radius = node.radius * reduction)
        
        var straight = Random.nextBoolean()
        var curve = curve(base, straight)
        list.add(branch(base, curve, smaller + curve, color, height, draw))
        
        straight = if (straight) false else Random.nextBoolean()
        curve = curve(base, straight)
        list.add(branch(base, curve, smaller - curve, color, height, draw))
        
        straight = if (straight) false else Random.nextBoolean()
        curve = curve(base, straight)
        list.add(branch(base, curve, smaller + curve * 2f, color, height, draw))
        
        straight = false
        curve = curve(base, straight)
        list.add(branch(base, curve, smaller - curve * 2f, color, height, draw))
    }
    
    foliage.add(nodes)
    if (smaller > 10f)
        branches(list, color, smaller, foliage, height, draw)
}

private fun curve(node: Node, straight: Boolean): Float {
    return if (straight && node.curve != null) - node.curve
    else real(next(5) + 1f)
}

private suspend fun foliage(
    foliage: List<List<Node>>,
    draw: (Color, Float, Path.() -> Unit) -> Unit
) {
    foliage.forEachIndexed { _, nodes ->
        delay(10L)
        val color = shaded(Color(0xFFCCEECC))
        val sizing = 5f
        nodes.forEach { node ->
            val multiplier = (node.angle * kotlin.math.PI / 180f).toFloat()
            val endX = node.x + real(sizing * cos(multiplier))
            val endY = node.y + real(sizing * sin(multiplier))
            leaf(node.x, node.y, sizing / 5f, endX, endY, color, draw)
        }
    }
}

private fun leaf(
    startX: Float,
    startY: Float,
    curve: Float,
    endX: Float,
    endY: Float,
    color: Color,
    draw: (Color, Float, Path.() -> Unit) -> Unit,
    inverted: Boolean = false
) {
    draw(color, 1f) {
        moveTo(startX, startY)
        cubicTo(startX, startY, startX + curve, endY + curve, endX, endY)
    }

    if (!inverted) {
        leaf(startX, startY, -curve, endX, endY, color, draw, true)
    }
}

private fun next(until: Int) : Float = Random.nextInt(until).toFloat()

private fun real(number: Float): Float = number * if (Random.nextBoolean()) -1f else 1f

private fun shaded(color: Color): Color {
    val percent = -0.2f * Random.nextFloat()
    return Color(
        red = (color.red * (1 + percent)).coerceIn(0f, 1f),
        green = (color.green * (1 + percent)).coerceIn(0f, 1f),
        blue = (color.blue * (1 + percent)).coerceIn(0f, 1f),
        alpha = color.alpha
    )
}

private fun tree(
    width: Float,
    height: Float,
    foliage: MutableList<List<Node>>,
    draw: (Color, Float, Path.() -> Unit) -> Unit
) {
    val sizing = height / ((Random.nextFloat() * 5f + 5f) * 2f) + next(50)
    val root = width * 0.5f + real(next(50) + 1f) * width * 0.01f
    val color = shaded(Color(0xFFDDEEDD))
    
    trunk(root, height, 10f, color, sizing, foliage, draw)
    trunk(root, height - sizing / 2f, 7f, color, sizing / 2f, foliage, draw)
    trunk(root, height - sizing / 2f - sizing / 3f, 3f, color, sizing / 3f, foliage, draw)
}

private fun trunk(
    root: Float,
    height: Float,
    radius: Float,
    color: Color,
    sizing: Float,
    foliage: MutableList<List<Node>>,
    draw: (Color, Float, Path.() -> Unit) -> Unit
) {
    val curve = real(next(5) + 1f)
    val trunkY = height - sizing - 50f
    val trunk = Node(root, trunkY, 90f, radius)
    
    draw(color, radius) {
        moveTo(root, height - 50f)
        cubicTo(root, height - 100f, root + curve, trunkY, root, trunkY)
    }
    
    branches(listOf(trunk), color, sizing, foliage, height, draw)
}
